import React, { useState, useCallback, useRef, useEffect } from 'react';
import { db } from '../data/store';
import { getAllAreas } from './ParamManager';
import { lists } from '../data/lists';
import ListEditor from './ListEditor';
import { SERVER_URL, MAPI_URL, syncedGet, syncedSet, beginUiEdit, endUiEdit } from '../data/serverStore';
import {
  contactEmails,
  getCompanyContacts,
  getClientProfiles,
  normalizeContacts,
  saveClientProfile,
} from '../data/clientProfiles';
import jsPDF from 'jspdf';

const today   = () => new Date().toISOString().split('T')[0];
const weekOut = () => new Date(Date.now()+7*86400000).toISOString().split('T')[0];
const nowTime = () => new Date().toTimeString().slice(0,5);

const blankSample = (o={}) => ({
  source:'', desc:'', client:'',
  contacts:[{name:'',email:''}],
  testCategory:'Specific Analysis', sampleForm:'Aqueous',
  rush:'5', tech:'apex', amount:'4 oz', solidLiq:'L',
  price:'', received:today(), taken:today(), dueDate:weekOut(),
  timeReceived:nowTime(), workOrder:'', cocAnalysis:'', params:[],
  ...o,
});

// ── COC download-as-PDF / print helpers ─────────────────────────────────────
// Physically rotates image pixels onto a canvas (not just CSS) so a
// downloaded PDF or print job matches whatever rotation is showing on screen.
function loadImageEl(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function rotateToCanvas(img, rotationDeg) {
  const sideways = rotationDeg % 180 !== 0;
  const w = sideways ? img.naturalHeight : img.naturalWidth;
  const h = sideways ? img.naturalWidth : img.naturalHeight;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.translate(w / 2, h / 2);
  ctx.rotate((rotationDeg * Math.PI) / 180);
  ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
  return canvas;
}

async function cocImageToPdf(dataUrl, rotationDeg) {
  const img = await loadImageEl(dataUrl);
  const canvas = rotateToCanvas(img, rotationDeg);
  const imgData = canvas.toDataURL('image/jpeg', 0.92);
  const isLandscape = canvas.width > canvas.height;
  const doc = new jsPDF({ orientation: isLandscape ? 'landscape' : 'portrait', unit: 'pt', format: 'letter' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 18; // 0.25in
  const scale = Math.min((pageW - margin * 2) / canvas.width, (pageH - margin * 2) / canvas.height);
  const drawW = canvas.width * scale;
  const drawH = canvas.height * scale;
  doc.addImage(imgData, 'JPEG', (pageW - drawW) / 2, (pageH - drawH) / 2, drawW, drawH);
  return doc;
}

// Prints via a hidden iframe — avoids popup blockers and keeps the user on
// the same page.
function printViaHiddenIframe(srcDoc) {
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  document.body.appendChild(iframe);
  iframe.onload = () => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch {
      /* user's browser may block programmatic print — they can still use View/PDF directly */
    }
    setTimeout(() => iframe.remove(), 2000);
  };
  iframe.srcdoc = srcDoc;
}

// ── Editable cell ─────────────────────────────────────────────────────────────
function Cell({ value, onChange, type='text', options, width }) {
  if (options) return (
    <select value={value} onChange={e=>onChange(e.target.value)}
      style={{width:width||'100%',border:'1px solid #cdd3e4',borderRadius:3,padding:'3px 4px',fontSize:11,outline:'none'}}>
      {options.map(o=><option key={o}>{o}</option>)}
    </select>
  );
  return (
    <input type={type} value={value} onChange={e=>onChange(e.target.value)}
      style={{width:width||'100%',border:'1px solid #cdd3e4',borderRadius:3,padding:'3px 5px',fontSize:11,outline:'none'}}/>
  );
}

// ── Combo dropdown ────────────────────────────────────────────────────────────
function ComboField({ label, value, onChange, options, placeholder, required }) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [rect, setRect] = useState(null);
  const wrapRef = React.useRef();
  const filtered = options.filter(o=>o.toLowerCase().includes(filter.toLowerCase()));
  const pick = (val) => { onChange(val); setOpen(false); setFilter(''); };
  const openDd = () => {
    if (wrapRef.current) setRect(wrapRef.current.getBoundingClientRect());
    setFilter(''); setOpen(true);
  };
  return (
    <div className="form-group" ref={wrapRef}>
      <label>{label}{required?' *':''}</label>
      <div style={{display:'flex',gap:0}}>
        <input value={value}
          onChange={e=>{onChange(e.target.value);setFilter(e.target.value);setOpen(true);if(wrapRef.current)setRect(wrapRef.current.getBoundingClientRect());}}
          onFocus={openDd} onBlur={()=>setTimeout(()=>setOpen(false),180)}
          placeholder={placeholder||''}
          style={{flex:1,border:'1px solid #cdd3e4',borderRadius:'4px 0 0 4px',padding:'6px 8px',fontSize:12,outline:'none'}}/>
        <button type="button" onMouseDown={e=>{e.preventDefault();open?setOpen(false):openDd();}}
          style={{border:'1px solid #cdd3e4',borderLeft:'none',borderRadius:'0 4px 4px 0',background:'#f4f6fb',padding:'0 10px',cursor:'pointer',fontSize:11}}>▾</button>
      </div>
      {open && filtered.length>0 && rect && (
        <div style={{position:'fixed',top:rect.bottom+2,left:rect.left,width:rect.width,zIndex:99999,
          background:'white',border:'2px solid #1e3060',borderRadius:6,maxHeight:200,overflowY:'auto',
          boxShadow:'0 8px 32px rgba(0,0,0,0.22)'}}>
          {filtered.map(opt=>(
            <div key={opt} onMouseDown={()=>pick(opt)}
              style={{padding:'6px 10px',fontSize:12,cursor:'pointer',background:opt===value?'#e8f0ff':'white',borderBottom:'1px solid #f4f4f4'}}
              onMouseEnter={e=>e.currentTarget.style.background='#f0f4ff'}
              onMouseLeave={e=>e.currentTarget.style.background=opt===value?'#e8f0ff':'white'}>
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function SampleReceiving({ onNavigate, onSelectSample, toast, isActive = false }) {
  // `header` holds shared defaults (source, dates, rush, tech, etc.) entered
  // once. Clicking "+ Add Sample Row" snapshots it into a new row, which can
  // then be edited per-sample independently (desc, test category, sample
  // form, date taken, price, params all vary per row).
  const [header, setHeader]         = useState(blankSample());
  const [rows, setRows]             = useState([]);
  const [cocImage, setCocImage]     = useState(null);
  const [cocId, setCocId]           = useState(null);   // most recently scanned/uploaded COC
  const [cocIds, setCocIds]         = useState([]);      // ALL COCs attached so far — a work order can have several
  const [scanning, setScanning]     = useState(false);
  const [ocrStatus, setOcrStatus]   = useState('');
  const [showListEditor, setShowListEditor] = useState(false);
  const [listsVersion, setListsVersion]     = useState(0);
  const [showCOC, setShowCOC]       = useState(false);
  const [cocRotation, setCocRotation] = useState(0);
  const [cocWorking, setCocWorking] = useState(false);
  const [scanners, setScanners]     = useState([]);
  const [showScanPicker, setShowScanPicker] = useState(false);
  const [showCOCList, setShowCOCList] = useState(false);
  const [savingAll, setSavingAll] = useState(false);
  const fileInputRef = useRef();

  useEffect(() => {
    const owner = 'sample-receiving-form';
    if (isActive) beginUiEdit(owner);
    else endUiEdit(owner);
    return () => endUiEdit(owner);
  }, [isActive]);

  const sampleNum = db.getCounters().nextSampleNum;
  const setH = (k,v) => setHeader(p=>({...p,[k]:v}));
  const onListsChanged = useCallback(()=>setListsVersion(v=>v+1),[]);

  const testCategories = [...new Set([...lists.getTestCategories(),...getAllAreas().map(a=>a.label)])];
  const sampleForms    = lists.getSampleForms();
  const sources        = [...new Set([
    ...lists.getSources(),
    ...getClientProfiles().map(profile => profile.company),
  ].filter(Boolean))].sort((a,b)=>a.localeCompare(b));
  const technicians    = lists.getTechnicians();

  const selectSourceCompany = company => {
    const loadedContacts = getCompanyContacts(company);
    setHeader(previous => {
      const contacts = loadedContacts.length
        ? loadedContacts
        : previous.source === company ? previous.contacts : [{name:'',email:''}];
      return {
        ...previous,
        source:company,
        client:contacts[0]?.name || '',
        contacts:contacts.length ? contacts : [{name:'',email:''}],
      };
    });
  };

  const setPrimaryContact = (field, value) => setHeader(previous => {
    const contacts = previous.contacts?.length
      ? previous.contacts.map(contact => ({...contact}))
      : [{name:'',email:''}];
    contacts[0] = {...contacts[0], [field]:value};
    return {
      ...previous,
      client:field === 'name' ? value : (contacts[0].name || ''),
      contacts,
    };
  });

  const setContact = (index, field, value) => setHeader(previous => ({
    ...previous,
    contacts:(previous.contacts || []).map((contact, contactIndex) =>
      contactIndex === index ? {...contact,[field]:value} : contact
    ),
  }));

  const addContact = () => setHeader(previous => ({
    ...previous,
    contacts:[...(previous.contacts || []),{name:'',email:''}],
  }));

  const removeContact = index => setHeader(previous => ({
    ...previous,
    contacts:(previous.contacts || []).filter((_, contactIndex) => contactIndex !== index),
  }));

  const addSourceCompany = async () => {
    const company = (header.source || '').trim();
    if (!company) {
      toast('error', 'Type a Source / Client company name first');
      return;
    }

    const existing = sources.find(source => source.toLowerCase() === company.toLowerCase());
    const canonical = existing || company;
    try {
      if (!existing) lists.addTo('sources', canonical);
      const profile = await saveClientProfile(canonical, { contacts:header.contacts });
      setHeader(previous => {
        const contacts = profile.contacts.length ? profile.contacts : [{name:'',email:''}];
        return {...previous,source:canonical,client:contacts[0]?.name || '',contacts};
      });
      setListsVersion(version => version + 1);
      toast('success', `${canonical} and its contact list were saved`);
    } catch (error) {
      toast('error', `Company was not saved: ${error.message}`);
    }
  };

  // App.jsx normally remounts all screens after background data sync.  That
  // is useful for refreshing ordinary screens, but it used to silently
  // destroy an in-progress scan session.  Hold remounting while a scan is
  // active.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('lims-workflow-busy', { detail: scanning }));
    return () => {
      if (scanning) {
        window.dispatchEvent(new CustomEvent('lims-workflow-busy', { detail: false }));
      }
    };
  }, [scanning]);

  const setRow = (i,k,v) => setRows(prev=>{const n=[...prev];n[i]={...n[i],[k]:v};return n;});
  const addRow = () => setRows(prev => {
    const previous = prev[prev.length - 1];
    const carriedForward = previous ? {
      received: previous.received,
      taken: previous.taken,
      sampleForm: previous.sampleForm,
      amount: previous.amount,
      rush: previous.rush,
    } : {};

    return [...prev, {
      ...header,
      ...carriedForward,
      desc: '',
      params: [],
    }];
  });
  const removeRow = i => setRows(prev=>prev.filter((_,j)=>j!==i));
  const clearAll = () => { setHeader(blankSample()); setRows([]); };

  // ── Scan ──────────────────────────────────────────────────────────────────
  const handleScan = async () => {
    setOcrStatus('Looking for connected scanners...');
    try {
      // Identify which agent workstation this browser is physically sitting
      // at, via the agent's own local HTTP server (MAPI_URL is localhost —
      // see server/serverStore.js — so this only succeeds if the Windows
      // agent is actually running on THIS machine).
      const agentRes = await fetch(`${MAPI_URL}/health`);
      const agent = await agentRes.json();
      if (!agent.workstation) throw new Error('Windows agent workstation was not detected.');

      // Ask the VPS bridge what scanners that specific agent currently sees,
      // instead of guessing device index 1. This is the actual device
      // enumeration step that was previously skipped entirely.
      const listRes = await fetch(
        `${SERVER_URL}/list-scanners?workstation=${encodeURIComponent(agent.workstation)}`
      );
      const listData = await listRes.json();
      if (listData.error) throw new Error(listData.error);

      const found = listData.scanners || [];
      if (found.length === 0) {
        throw new Error('No scanner was found on this computer. Check it is powered on and connected, then try again.');
      }

      setScanners(found);
      setOcrStatus('');

      if (found.length === 1) {
        // Only one device — no need to make the person choose.
        await doScan(found[0]);
      } else {
        // Multiple devices connected to this workstation — let the person
        // pick which one, via the existing picker modal below.
        setShowScanPicker(true);
      }
    } catch(e) {
      setOcrStatus('');
      toast('error', e.message || 'Cannot contact the Sterling Windows Agent.');
    }
  };

  const doScan = async (scanner) => {
    setShowScanPicker(false);
    setScanning(true);
    setOcrStatus(`Scanning with: ${scanner.name}...`);
    // If the scanner/WIA driver gets stuck ("device busy" from an
    // incompletely-released previous session), the backend request can hang
    // indefinitely with no response — which used to leave "Scan COC"
    // disabled until a full page reload. Bound it client-side so the button
    // always recovers and the person gets a clear, actionable error instead.
    //
    // IMPORTANT: this must stay ABOVE the server-side scannerBridge timeout
    // (75s as of this writing, see server/scannerBridge.js timeoutMs). A scan
    // triggered with idx:'prompt' opens a native Windows scan dialog on the
    // agent machine that a person has to interact with (pick device, adjust
    // settings, click Scan) — routinely taking well over 30s. If the client
    // aborts first, it looks and reads exactly like a stuck/broken scanner
    // even though the agent was still working correctly and would have
    // succeeded a few seconds later. Give it real headroom past 75s so the
    // backend's own timeout — not this one — is what fires on genuine
    // failures, and so its error message (not this generic one) is shown.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 90000);
    try {
      const res = await fetch(`${SERVER_URL}/scan-coc`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({
          deviceIndex: scanner.idx,
          workstation: scanner.workstation,
        }),
        signal: controller.signal,
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error||'Scan failed');
      setCocImage(data.image);
      setCocId(data.cocId);
      setCocIds(prev => prev.includes(data.cocId) ? prev : [...prev, data.cocId]);
      setOcrStatus('Scan attached — add sample rows below');
      toast('success', 'Scan complete — add sample rows below');
    } catch(e) {
      const timedOut = e.name === 'AbortError';
      toast('error', timedOut
        ? 'No response from the scanner after 90 seconds. If a scan dialog is open on the scanner computer, finish or cancel it there. If nothing is open, the Windows agent may be stuck — try again, and power-cycle the scanner if it keeps happening.'
        : e.message);
      setOcrStatus('');
    } finally {
      clearTimeout(timeoutId);
      setScanning(false);
    }
  };

  // ── Upload ────────────────────────────────────────────────────────────────
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // A PDF data URL cannot be decoded by HTMLImageElement, which the COC
    // viewer below relies on. Reject them explicitly instead of saving PDF
    // bytes under a misleading .jpg filename and having the viewer fail.
    if (file.type === 'application/pdf' || /\.pdf$/i.test(file.name)) {
      toast('error', 'PDF COCs are not supported here. Export the page as JPG or PNG, then upload it.');
      e.target.value = '';
      return;
    }
    setScanning(true);
    setOcrStatus('Reading file...');
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const img = ev.target.result;
      setCocImage(img);
      try {
        const res = await fetch(`${SERVER_URL}/upload-coc`, {
          method:'POST', headers:{'Content-Type':'application/json'},
          body: JSON.stringify({ image: img, fileName: file.name }),
        });
        const data = await res.json();
        if (data.cocId) {
          setCocId(data.cocId);
          setCocIds(prev => prev.includes(data.cocId) ? prev : [...prev, data.cocId]);
        }
      } catch {}
      setOcrStatus('Upload attached — add sample rows below');
      toast('success', 'Upload complete — add sample rows below');
      setScanning(false);
    };
    reader.readAsDataURL(file);
  };

  // ── Persist which COC scans belong to a work order ────────────────────────
  // A work order can accumulate multiple COC scans over time (e.g. samples
  // added later under a supplemental COC) — this merges new scan IDs into
  // whatever's already on record for that WO rather than overwriting it.
  const registerCOCsForWorkOrder = (wo, newIds) => {
    const ids = (newIds||[]).filter(Boolean);
    if (!wo || ids.length === 0) return;
    const map = syncedGet('coc_by_wo', {}) || {};
    const existing = map[wo] || [];
    const merged = [...new Set([...existing, ...ids])];
    syncedSet('coc_by_wo', { ...map, [wo]: merged });
  };

  // ── Save all samples ───────────────────────────────────────────────────────
  const handleSaveAll = async () => {
    if (savingAll) return;
    if (rows.length === 0) { toast('error','Add at least one sample row first'); return; }
    if (!header.source) { toast('error','Client/Source is required'); return; }
    const manualWo = String(header.workOrder || rows[0]?.workOrder || '').trim();
    const existingWoSamples = manualWo
      ? db.getSamples().filter(sample =>
          String(sample.workOrder || '').trim().toLowerCase() === manualWo.toLowerCase()
        )
      : [];
    if (existingWoSamples.length > 0) {
      const existingSource = String(existingWoSamples[0]?.source || existingWoSamples[0]?.client || '').trim();
      const newSource = String(header.source || '').trim();
      if (existingSource && newSource && existingSource.toLowerCase() !== newSource.toLowerCase()) {
        toast('error', `Work Order ${manualWo} already belongs to ${existingSource}. Clear the Work Order field to auto-assign a new number.`);
        return;
      }
      const confirmed = window.confirm(
        `Work Order ${manualWo} already exists with Sample${existingWoSamples.length === 1 ? '' : 's'} ` +
        `${existingWoSamples.map(sample => sample.id).join(', ')}.\n\n` +
        'Continue only if these new samples intentionally belong to that same Work Order.\n' +
        'Choose Cancel, clear the Work Order field, and save again to receive a new automatic number.'
      );
      if (!confirmed) return;
    }
    setSavingAll(true);
    let wo;
    let sampleIds;
    try {
      // Automatic work-order numbers are reserved atomically by the server.
      // A value typed by the user is normalized only after the existing-WO
      // warning above has made an intentional association explicit.
      wo = manualWo || await db.nextWorkOrder();
      sampleIds = await db.reserveSampleNums(rows.length);
    } catch (error) {
      toast('error', `Could not reserve sample numbers: ${error.message}`);
      setSavingAll(false);
      return;
    }
    let lastSample = null;
    try {
      const savedContacts = normalizeContacts(header.contacts);
      await saveClientProfile(header.source, { contacts:savedContacts });
      for (let index = 0; index < rows.length; index += 1) {
        const r = rows[index];
        const id = sampleIds[index];
        const s = buildSample({...r, source:header.source, client:header.source, workOrder:wo, cocId:cocId||undefined}, id, wo);
        await db.addSample(s);
        if (!sources.includes(s.source)) lists.addTo('sources', s.source);
        lastSample = s;
      }
      registerCOCsForWorkOrder(wo, cocIds);
      if (!lastSample) return;
      onSelectSample(lastSample);
      toast('success', `${rows.length} sample(s) received — WO: ${wo}${cocIds.length>1?` (${cocIds.length} COC scans attached)`:''}`);
      // Reset the page so it's blank next time — this screen stays mounted
      // in the background rather than unmounting on navigation, so without
      // this the just-saved header/rows/COC would still be sitting here.
      // Also explicitly reset scan-related flags: if any of these were ever
      // left stuck true from a prior scan, "Scan COC" would silently do
      // nothing on the next work order (disabled buttons don't fire onClick
      // at all — no error, no visible change) rather than showing an error.
      setHeader(blankSample());
      setRows([]);
      setCocImage(null);
      setCocId(null);
      setCocIds([]);
      setOcrStatus('');
      setScanning(false);
      setShowScanPicker(false);
      setScanners([]);
      setTimeout(()=>onNavigate('setup'), 800);
    } catch (error) {
      toast('error', error.message || 'Could not save the samples');
    } finally {
      setSavingAll(false);
    }
  };

  function buildSample(f, id, wo) {
    return {
      id, workOrder:wo,
      rush: parseInt(f.rush)||null,
      received:f.received, taken:f.taken, dueDate:f.dueDate,
      source:f.source, desc:f.desc||'',
      testType:(f.testCategory||'').includes('Fuel')?'Fuel':(f.testCategory||'').includes('Coal')?'Coal':'Specific',
      price:parseFloat(f.price)||0,
      billed:false, completed:false, dateCompleted:'', invoiceNum:'',
      // Keep `client` as the company for billing, history, and legacy report
      // grouping. The person's name is stored separately in `attention`.
      client:f.source,
      testCategory:f.testCategory||'Specific Analysis',
      sampleForm:f.sampleForm||'Aqueous',
      tech:f.tech||'apex', amount:f.amount||'4 oz',
      solidLiq:f.solidLiq||'L',
      contacts:normalizeContacts(header.contacts),
      attention:header.client || normalizeContacts(header.contacts)[0]?.name || '',
      clientEmail:contactEmails(header.contacts),
      params:f.params||[], results:{},
      cocId:f.cocId||cocId||undefined,
      cocAnalysis:f.cocAnalysis||undefined,
    };
  }

  // ── COC: download as PDF / print (respects current rotation) ─────────────
  const handleDownloadCocPdf = async () => {
    if (!cocImage) return;
    setCocWorking(true);
    try {
      const doc = await cocImageToPdf(cocImage, cocRotation);
      doc.save(`COC-${cocId || 'scan'}.pdf`);
    } catch {
      toast('error', 'Could not generate the PDF for this COC.');
    } finally {
      setCocWorking(false);
    }
  };

  const handlePrintCoc = async () => {
    if (!cocImage) return;
    setCocWorking(true);
    try {
      const img = await loadImageEl(cocImage);
      const canvas = rotateToCanvas(img, cocRotation);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
      const srcDoc = `<!doctype html><html><head><style>
        @page { margin: 0.25in; }
        html,body{margin:0;padding:0;}
        img{display:block;width:100%;height:auto;}
      </style></head><body><img src="${dataUrl}"/></body></html>`;
      printViaHiddenIframe(srcDoc);
    } catch {
      toast('error', 'Could not prepare this COC for printing.');
    } finally {
      setCocWorking(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Scanner picker */}
      {showScanPicker && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.6)',zIndex:3000,
          display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div style={{background:'white',borderRadius:8,padding:24,width:380,
            boxShadow:'0 20px 60px rgba(0,0,0,.3)'}}>
            <div style={{fontWeight:700,fontSize:14,marginBottom:16,color:'#1a2744'}}>
              📷 Select Scanner
            </div>
            {scanners.map(s=>(
              <button key={s.idx} onClick={()=>doScan(s)}
                style={{display:'block',width:'100%',textAlign:'left',padding:'12px 16px',
                  marginBottom:8,border:'1px solid #cdd3e4',borderRadius:6,cursor:'pointer',
                  background:'white',fontSize:13,fontWeight:500,color:'#1a2744'}}
                onMouseEnter={e=>e.currentTarget.style.background='#eff6ff'}
                onMouseLeave={e=>e.currentTarget.style.background='white'}>
                🖨 {s.name}
              </button>
            ))}
            <button onClick={()=>setShowScanPicker(false)}
              style={{width:'100%',padding:'8px',marginTop:4,border:'1px solid #cdd3e4',
                borderRadius:6,cursor:'pointer',background:'#f8f9fa',fontSize:12}}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* COC Viewer */}
      {showCOC && cocImage && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.75)',zIndex:3000,
          display:'flex',alignItems:'center',justifyContent:'center'}}
          onClick={()=>setShowCOC(false)}>
          <div style={{background:'white',borderRadius:8,padding:16,maxWidth:'95vw',maxHeight:'95vh',
            overflow:'auto',boxShadow:'0 20px 60px rgba(0,0,0,.4)'}}
            onClick={e=>e.stopPropagation()}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8,gap:8}}>
              <strong>Chain of Custody</strong>
              <div style={{display:'flex',gap:6}}>
                <button onClick={()=>setCocRotation(r=>(r-90+360)%360)}
                  style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'4px 10px',cursor:'pointer',fontSize:12}}>
                  ↺ Rotate Left
                </button>
                <button onClick={()=>setCocRotation(r=>(r+90)%360)}
                  style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'4px 10px',cursor:'pointer',fontSize:12}}>
                  ↻ Rotate Right
                </button>
                <button onClick={handleDownloadCocPdf} disabled={cocWorking}
                  style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'4px 10px',
                    cursor:cocWorking?'default':'pointer',fontSize:12,fontWeight:600,background:'white'}}>
                  {cocWorking ? '...' : '⬇ PDF'}
                </button>
                <button onClick={handlePrintCoc} disabled={cocWorking}
                  style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'4px 10px',
                    cursor:cocWorking?'default':'pointer',fontSize:12,fontWeight:600,background:'white'}}>
                  🖨 Print
                </button>
                <button onClick={()=>setShowCOC(false)}
                  style={{border:'none',background:'none',fontSize:18,cursor:'pointer',color:'#666'}}>✕</button>
              </div>
            </div>
            <div style={{overflow:'auto',maxHeight:'80vh',textAlign:'center'}}>
              <img src={cocImage} alt="COC"
                style={{maxWidth: cocRotation===90||cocRotation===270 ? '80vh' : '100%',
                  maxHeight: cocRotation===90||cocRotation===270 ? '100%' : '80vh',
                  objectFit:'contain',
                  transform:`rotate(${cocRotation}deg)`,
                  transformOrigin:'center center',
                  transition:'transform 0.3s'}}/>
            </div>
          </div>
        </div>
      )}

      {/* COCs attached so far — everything here will be attached to the work order on save */}
      {showCOCList && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.6)',zIndex:3000,
          display:'flex',alignItems:'center',justifyContent:'center'}}
          onClick={()=>setShowCOCList(false)}>
          <div style={{background:'white',borderRadius:8,padding:20,width:420,maxHeight:'80vh',
            overflowY:'auto',boxShadow:'0 20px 60px rgba(0,0,0,.4)'}}
            onClick={e=>e.stopPropagation()}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
              <strong style={{fontSize:14}}>📎 COC Scans Attached</strong>
              <button onClick={()=>setShowCOCList(false)}
                style={{border:'none',background:'none',fontSize:18,cursor:'pointer',color:'#666'}}>✕</button>
            </div>
            <div className="notice notice-info" style={{marginBottom:12,fontSize:11}}>
              All of these will be attached to Work Order {header.workOrder || '(auto-assigned)'} when you save — a work order can have more than one COC.
            </div>
            {cocIds.map((id,i) => (
              <div key={id} style={{display:'flex',alignItems:'center',gap:8,padding:'6px 8px',
                border:'1px solid #e0e4ec',borderRadius:6,marginBottom:6}}>
                <img src={`${SERVER_URL}/coc/${id}`} alt="" style={{width:44,height:44,objectFit:'cover',borderRadius:4,border:'1px solid #ddd'}}/>
                <span style={{flex:1,fontSize:12,fontFamily:'monospace'}}>Scan {i+1} — {id}</span>
                <button onClick={()=>setCocIds(prev=>prev.filter(x=>x!==id))}
                  title="Remove this scan from the batch"
                  style={{background:'none',border:'none',color:'#dc2626',cursor:'pointer',fontSize:14}}>✕</button>
              </div>
            ))}
            {cocIds.length===0 && (
              <div style={{textAlign:'center',color:'#aaa',padding:16,fontSize:12}}>No COCs attached yet</div>
            )}
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="toolbar">
        <button className="btn" style={{background:'#1a6b42',color:'white',borderColor:'#1a6b42'}}
          onClick={handleScan} disabled={scanning}>
          {scanning ? '⏳ Scanning...' : '📷 Scan COC'}
        </button>
        <button className="btn" onClick={()=>fileInputRef.current?.click()} disabled={scanning}>
          📁 Upload COC
        </button>
        <input ref={fileInputRef} type="file" accept="image/*,.jpg,.jpeg,.png" style={{display:'none'}} onChange={handleUpload}/>
        {cocImage && (
          <button className="btn" onClick={()=>setShowCOC(true)}>
            👁 View COC
          </button>
        )}
        {cocIds.length > 0 && (
          <button className="btn"
            style={{background:cocIds.length>1?'#fff8e8':'white',borderColor:cocIds.length>1?'#e0c060':undefined,
              color:cocIds.length>1?'#7a5a00':undefined,fontWeight:cocIds.length>1?700:400}}
            onClick={()=>setShowCOCList(true)}
            title="Every COC scan will be attached to this work order when you save">
            📎 {cocIds.length} COC{cocIds.length!==1?'s':''} attached
          </button>
        )}
        {ocrStatus && (
          <span style={{fontSize:11,color:'#1a6b42',fontWeight:600,marginLeft:4}}>{ocrStatus}</span>
        )}
        <div className="tb-sep"/>
        <button className="btn" onClick={()=>onNavigate('log')}>← Back to Log</button>
        <button className="btn" style={{background:'#fff8e8',borderColor:'#e0c060',color:'#7a5a00'}}
          onClick={()=>setShowListEditor(true)}>⚙️ Edit Lists</button>
        <button className="btn" onClick={clearAll}>🔄 Clear</button>
        <div className="tb-sep"/>
        <span className="tb-label">New Sample #:</span>
        <span className="tb-value mono">{sampleNum}</span>
        <div style={{flex:1}}/>
        <button className="btn btn-primary" onClick={handleSaveAll} disabled={rows.length===0 || savingAll}>
          {savingAll ? '⏳ Saving…' : `✅ Save ${rows.length>0?`(${rows.length})`:''} Sample${rows.length!==1?'s':''} & Go to Setup`}
        </button>
      </div>

      {/* Header — shared defaults, entered once. New rows snapshot these values. */}
      <div className="content" style={{padding:12,paddingBottom:0}}>
        <div className="panel">
          <div className="panel-header">📋 Sample Header Information</div>
          <div className="panel-body">

            <div className="form-section-title">Identification</div>
            <div className="form-grid">
              <div className="form-group"><label>Work Order</label>
                <input placeholder="Auto-assign or enter 26-XXXX" value={header.workOrder}
                  onChange={e=>setH('workOrder',e.target.value)}/>
              </div>
              <div className="form-group"><label>Rush Priority (1-10)</label>
                <input type="number" min="1" max="10" value={header.rush} onChange={e=>setH('rush',e.target.value)}/>
              </div>
              <ComboField label="Assigned To" value={header.tech} onChange={v=>setH('tech',v)} options={technicians} placeholder="apex"/>
            </div>

            <div className="form-section-title" style={{marginTop:14}}>
              Source, Client &amp; Sample Defaults
              <span style={{fontWeight:400,color:'#999',fontSize:10.5,marginLeft:6}}>(choose the test category separately in each sample row below)</span>
              <button className="btn btn-sm" style={{marginLeft:10,background:'#fff8e8',borderColor:'#e0c060',color:'#7a5a00'}}
                onClick={()=>setShowListEditor(true)}>⚙️ Edit Sources</button>
              <button className="btn btn-sm" style={{marginLeft:6,background:'#fff8e8',borderColor:'#e0c060',color:'#7a5a00'}}
                onClick={()=>setShowListEditor(true)}>⚙️ Edit Categories</button>
            </div>
            <div className="form-grid">
              <div>
                <ComboField label="Company / Source" value={header.source} onChange={selectSourceCompany}
                  options={sources} placeholder="Type or select company name" required/>
                <button type="button" className="btn btn-sm" onClick={addSourceCompany}
                  style={{marginTop:5,background:'#e8f0ff',borderColor:'#8aa7dc',color:'#1e4180'}}>
                  + Add Company to Dropdown
                </button>
              </div>
              <div className="form-group"><label>Client Name</label>
                <input value={header.client} onChange={e=>setPrimaryContact('name',e.target.value)} placeholder="Contact person"/>
              </div>
              <div className="form-group"><label>Email Address</label>
                <input type="email" value={header.contacts?.[0]?.email || ''}
                  onChange={e=>setPrimaryContact('email',e.target.value)} placeholder="name@company.com"/>
              </div>
            </div>

            <div className="form-section-title" style={{marginTop:14}}>Additional Company Contacts</div>
            <div style={{fontSize:10,color:'#6b7280',marginBottom:6}}>
              The client name and email above are remembered for this company. Add more reporting contacts here when needed.
            </div>
            {(header.contacts || []).slice(1).map((contact,additionalIndex)=>{
              const index = additionalIndex + 1;
              return (
              <div key={index} style={{display:'grid',gridTemplateColumns:'1fr 1.35fr auto',gap:8,alignItems:'end',marginBottom:6}}>
                <div className="form-group"><label>Contact Name</label>
                  <input value={contact.name||''} onChange={e=>setContact(index,'name',e.target.value)} placeholder="Name"/>
                </div>
                <div className="form-group"><label>Email Address</label>
                  <input type="email" value={contact.email||''} onChange={e=>setContact(index,'email',e.target.value)} placeholder="name@company.com"/>
                </div>
                <button type="button" className="btn btn-sm" onClick={()=>removeContact(index)}>Remove</button>
              </div>
            )})}
            <button type="button" className="btn btn-sm" onClick={addContact}>+ Add Another Contact</button>

            <div className="form-section-title" style={{marginTop:14}}>Dates & Times</div>
            <div className="form-grid">
              <div className="form-group"><label>Date Received</label>
                <input type="date" value={header.received} onChange={e=>setH('received',e.target.value)}/>
              </div>
              <div className="form-group"><label>Time Received</label>
                <input type="time" value={header.timeReceived} onChange={e=>setH('timeReceived',e.target.value)}/>
              </div>
              <div className="form-group"><label>Due Date</label>
                <input type="date" value={header.dueDate} onChange={e=>setH('dueDate',e.target.value)}/>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Samples table */}
      <div className="content" style={{padding:12}}>
        {rows.length > 0 ? (
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
              <thead>
                <tr style={{background:'#1a2744',color:'white'}}>
                  {['#','Description','Matrix','Result Profile','Test Category','Date Taken','Date Received','Due Date','Rush','Tech','Amount','WO','Price','Params',''].map(h=>(
                    <th key={h} style={{padding:'7px 8px',textAlign:'left',whiteSpace:'nowrap'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r,i)=>(
                  <tr key={i} style={{background:i%2===0?'white':'#f8fafc',borderBottom:'1px solid #e8ecf8'}}>
                    <td style={{padding:'4px 8px',fontWeight:700,color:'#1a2744'}}>{sampleNum+i}</td>
                    <td style={{padding:'4px 6px'}}><Cell value={r.desc} onChange={v=>setRow(i,'desc',v)}/></td>
                    <td style={{padding:'4px 6px'}}><Cell value={r.sampleForm} onChange={v=>setRow(i,'sampleForm',v)} options={sampleForms}/></td>
                    <td style={{padding:'4px 6px'}}><Cell value={r.solidLiq} onChange={v=>setRow(i,'solidLiq',v)} options={['L','S','T']} width="70px"/></td>
                    <td style={{padding:'4px 6px'}}><Cell value={r.testCategory} onChange={v=>setRow(i,'testCategory',v)} options={testCategories}/></td>
                    <td style={{padding:'4px 6px'}}><Cell value={r.taken} onChange={v=>setRow(i,'taken',v)} type="date"/></td>
                    <td style={{padding:'4px 6px'}}><Cell value={r.received} onChange={v=>setRow(i,'received',v)} type="date"/></td>
                    <td style={{padding:'4px 6px'}}><Cell value={r.dueDate} onChange={v=>setRow(i,'dueDate',v)} type="date"/></td>
                    <td style={{padding:'4px 6px'}}><Cell value={r.rush} onChange={v=>setRow(i,'rush',v)} width="50px"/></td>
                    <td style={{padding:'4px 6px'}}><Cell value={r.tech} onChange={v=>setRow(i,'tech',v)} options={technicians}/></td>
                    <td style={{padding:'4px 6px'}}><Cell value={r.amount} onChange={v=>setRow(i,'amount',v)} width="60px"/></td>
                    <td style={{padding:'4px 6px'}}><Cell value={r.workOrder} onChange={v=>setRow(i,'workOrder',v)} width="80px" placeholder="Auto"/></td>
                    <td style={{padding:'4px 6px'}}><Cell value={r.price} onChange={v=>setRow(i,'price',v)} type="number" width="65px"/></td>
                    <td style={{padding:'4px 6px'}}>
                      <input value={(r.params||[]).join(', ')}
                        onChange={e=>setRow(i,'params', e.target.value.split(',').map(s=>s.trim()).filter(Boolean))}
                        title={(r.params||[]).join(', ') || 'No parameters set — add manually or set in Sample Setup'}
                        placeholder="Params (comma-separated)..."
                        style={{width:170,border:(r.params||[]).length?'1px solid #86c98e':'1px solid #cdd3e4',
                          borderRadius:3,padding:'3px 5px',fontSize:10.5,outline:'none',
                          background:(r.params||[]).length?'#f2fbf3':'white'}}/>
                      {(r.params||[]).length>0 && (
                        <div style={{fontSize:9,color:'#1a6b42',marginTop:2}}>✓ {r.params.length} set</div>
                      )}
                    </td>
                    <td style={{padding:'4px 6px',textAlign:'center'}}>
                      <button onClick={()=>removeRow(i)}
                        style={{background:'none',border:'none',color:'#dc2626',cursor:'pointer',fontSize:14}}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{textAlign:'center',padding:'48px',color:'#aaa'}}>
            <div style={{fontSize:36,marginBottom:12}}>📄</div>
            <div style={{fontSize:14,marginBottom:8}}>No samples yet</div>
            <div style={{fontSize:12}}>Fill in the header above (optional), then click <strong>+ Add Sample Row</strong> below — or <strong>Scan COC</strong> / <strong>Upload COC</strong> to attach a chain of custody first</div>
          </div>
        )}

        <button onClick={addRow}
          style={{marginTop:10,background:'white',border:'1px dashed #1a2744',color:'#1a2744',
            borderRadius:4,padding:'6px 16px',cursor:'pointer',fontSize:12,fontWeight:600}}>
          + Add Sample Row
        </button>
      </div>

      {showListEditor && (
        <ListEditor onClose={()=>setShowListEditor(false)} onChanged={onListsChanged}/>
      )}
    </>
  );
}
